/**
 * Authentication Context
 *
 * Provides global authentication state and methods throughout the application.
 * Uses React Context API for state management without external dependencies.
 *
 * FEATURES:
 * - Global authentication state
 * - Protected route support
 * - User session management
 * - Auto-logout on token expiry
 * - Persistent login across page refreshes
 * - Role-based access control support
 *
 * FUTURE ENHANCEMENTS:
 * - Integration with Redux/Zustand for complex state
 * - WebSocket connection for real-time auth events
 * - Multi-tab synchronization for logout
 * - User activity tracking
 * - Session timeout warnings
 *
 * USAGE:
 * ```jsx
 * // Wrap app with provider
 * <AuthProvider>
 *   <App />
 * </AuthProvider>
 *
 * // Use in components
 * const { user, login, logout, isAuthenticated } = useAuth()
 * ```
 *
 * @module AuthContext
 */

import React, { createContext, useState, useEffect, useContext, useCallback } from 'react'
import authService from '@services/authService'

/**
 * Authentication Context
 * Holds authentication state and methods
 */
const AuthContext = createContext({
	// State
	user: null,
	isAuthenticated: false,
	isLoading: true,

	// Methods
	login: async () => {},
	logout: async () => {},
	register: async () => {},
	updateUser: () => {},
	checkAuth: () => {}
})

/**
 * Authentication Provider Component
 *
 * Manages authentication state and provides it to child components.
 * Handles initialization, login, logout, and session persistence.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element} Provider component
 */
export const AuthProvider = ({ children }) => {
	// ============================================================================
	// STATE MANAGEMENT
	// ============================================================================

	// User data state
	const [user, setUser] = useState(null)

	// Authentication status
	const [isAuthenticated, setIsAuthenticated] = useState(false)

	// Loading state (prevents flash of login screen during initialization)
	const [isLoading, setIsLoading] = useState(true)

	// ============================================================================
	// INITIALIZATION
	// ============================================================================

	/**
	 * Initialize authentication state on mount
	 * Checks for existing session and validates token
	 */
	useEffect(() => {
		const initAuth = async () => {
			try {
				// Check if user has valid session
				if (authService.isUserAuthenticated()) {
					const userData = authService.getCurrentUser()
					setUser(userData)
					setIsAuthenticated(true)
				} else {
					// No valid session
					setUser(null)
					setIsAuthenticated(false)
				}
			} catch (error) {
				console.error('Auth initialization error:', error)
				setUser(null)
				setIsAuthenticated(false)
			} finally {
				// Always stop loading
				setIsLoading(false)
			}
		}

		initAuth()
	}, [])

	/**
	 * Listen for storage events (multi-tab synchronization)
	 * Logout from all tabs when user logs out from one
	 */
	useEffect(() => {
		const handleStorageChange = (e) => {
			// Check if auth token was removed (logout in another tab)
			if (e.key === 'auth_access_token' && e.newValue === null) {
				handleLogout()
			}
		}

		// Listen to storage events
		window.addEventListener('storage', handleStorageChange)

		// Cleanup
		return () => {
			window.removeEventListener('storage', handleStorageChange)
		}
	}, [])

	// ============================================================================
	// AUTHENTICATION METHODS
	// ============================================================================

	/**
	 * Login user
	 *
	 * @param {Object} credentials - Login credentials
	 * @param {string} credentials.email - User email
	 * @param {string} credentials.password - User password
	 * @param {boolean} credentials.rememberMe - Whether to persist session
	 * @returns {Promise<Object>} User data
	 * @throws {Error} If login fails
	 */
	const handleLogin = useCallback(async (credentials) => {
		try {
			setIsLoading(true)

			// Call auth service
			const { user: userData } = await authService.login(credentials)

			// Update context state
			setUser(userData)
			setIsAuthenticated(true)

			return userData
		} catch (error) {
			// Re-throw error for component to handle
			throw error
		} finally {
			setIsLoading(false)
		}
	}, [])

	/**
	 * Register new user
	 *
	 * @param {Object} userData - User registration data
	 * @returns {Promise<Object>} User data
	 * @throws {Error} If registration fails
	 */
	const handleRegister = useCallback(async (userData) => {
		try {
			setIsLoading(true)

			// Call auth service
			const { user: newUser } = await authService.register(userData)

			// Update context state
			setUser(newUser)
			setIsAuthenticated(true)

			return newUser
		} catch (error) {
			throw error
		} finally {
			setIsLoading(false)
		}
	}, [])

	/**
	 * Logout user
	 *
	 * @param {boolean} allDevices - Whether to logout from all devices
	 * @returns {Promise<void>}
	 */
	const handleLogout = useCallback(async (allDevices = false) => {
		try {
			setIsLoading(true)

			// Call auth service
			await authService.logout(allDevices)

			// Clear context state
			setUser(null)
			setIsAuthenticated(false)
		} catch (error) {
			console.error('Logout error:', error)
			// Clear state even if API call fails
			setUser(null)
			setIsAuthenticated(false)
		} finally {
			setIsLoading(false)
		}
	}, [])

	/**
	 * Update user data in context
	 * Useful after profile updates
	 *
	 * @param {Object} updatedUser - Updated user data
	 */
	const updateUser = useCallback((updatedUser) => {
		setUser(prevUser => ({
			...prevUser,
			...updatedUser
		}))

		// Also update in auth service storage
		const rememberMe = authService.isTokenPersistent()
		authService.setUserData({ ...user, ...updatedUser }, rememberMe)
	}, [user])

	/**
	 * Check authentication status
	 * Validates token and refreshes if needed
	 *
	 * @returns {boolean} True if authenticated
	 */
	const checkAuth = useCallback(() => {
		return authService.isUserAuthenticated()
	}, [])

	// ============================================================================
	// ROLE-BASED ACCESS CONTROL (RBAC)
	// ============================================================================

	/**
	 * Check if user has specific role
	 *
	 * @param {string|string[]} roles - Required role(s)
	 * @returns {boolean} True if user has role
	 */
	const hasRole = useCallback((roles) => {
		if (!user || !user.role) return false

		const requiredRoles = Array.isArray(roles) ? roles : [roles]
		return requiredRoles.includes(user.role)
	}, [user])

	/**
	 * Check if user has specific permission
	 * Future enhancement: Implement granular permissions
	 *
	 * @param {string} permission - Required permission
	 * @returns {boolean} True if user has permission
	 */
	const hasPermission = useCallback((permission) => {
		if (!user || !user.permissions) return false

		return user.permissions.includes(permission)
	}, [user])

	// ============================================================================
	// CONTEXT VALUE
	// ============================================================================

	const value = {
		// State
		user,
		isAuthenticated,
		isLoading,

		// Authentication methods
		login: handleLogin,
		logout: handleLogout,
		register: handleRegister,

		// Utility methods
		updateUser,
		checkAuth,

		// Role-based access control
		hasRole,
		hasPermission
	}

	// ============================================================================
	// RENDER
	// ============================================================================

	return (
		<AuthContext.Provider value={value}>
			{children}
		</AuthContext.Provider>
	)
}

/**
 * Custom hook to use authentication context
 *
 * Provides access to authentication state and methods.
 * Must be used within AuthProvider.
 *
 * @returns {Object} Authentication context value
 * @throws {Error} If used outside AuthProvider
 *
 * @example
 * ```jsx
 * function LoginButton() {
 *   const { login, isLoading } = useAuth()
 *
 *   const handleLogin = async () => {
 *     await login({ email: '...', password: '...' })
 *   }
 *
 *   return <button onClick={handleLogin}>Login</button>
 * }
 * ```
 */
export const useAuth = () => {
	const context = useContext(AuthContext)

	if (context === undefined) {
		throw new Error('useAuth must be used within an AuthProvider')
	}

	return context
}

/**
 * Higher-Order Component for protected routes
 *
 * Wraps components that require authentication.
 * Redirects to login if user is not authenticated.
 *
 * @param {React.Component} Component - Component to protect
 * @param {Object} options - Protection options
 * @param {string[]} options.roles - Required roles
 * @param {string} options.redirectTo - Redirect path if unauthorized
 * @returns {React.Component} Protected component
 *
 * @example
 * ```jsx
 * const ProtectedDashboard = withAuth(Dashboard, { roles: ['admin'] })
 * ```
 */
export const withAuth = (Component, options = {}) => {
	return function ProtectedComponent(props) {
		const { isAuthenticated, isLoading, hasRole } = useAuth()
		const { roles, redirectTo = '/login' } = options

		// Show loading state
		if (isLoading) {
			return (
				<div className="flex-center" style={{ minHeight: '100vh' }}>
					<div className="loading-spinner" />
				</div>
			)
		}

		// Check authentication
		if (!isAuthenticated) {
			// Redirect to login
			window.location.href = redirectTo
			return null
		}

		// Check role-based access
		if (roles && !hasRole(roles)) {
			// Redirect to unauthorized page
			window.location.href = '/unauthorized'
			return null
		}

		// Render protected component
		return <Component {...props} />
	}
}

export default AuthContext
